<section class="operations" aria-labelledby="operations-title">
  <header class="operations-header">
    <div>
      <h2 id="operations-title">Jobs &amp; outbox</h2>
      <p>Inspect committed work, delivery attempts and authorized recovery.</p>
    </div>
    <button
      uiButton
      type="button"
      (click)="refresh()"
      [disabled]="busy || !canRead()"
    >
      Refresh monitor
    </button>
  </header>
  <p *ngIf="!canRead() || accessDenied" class="operations-error" role="alert">
    You do not currently have access to the operations monitor. Entered recovery
    reasons are kept.
  </p>
  <ng-container *ngIf="canRead() && !accessDenied">
    <section class="operations-summary" aria-label="Outbox progress">
      <p *ngIf="loading.summary" role="status">
        Loading committed-work summary…
      </p>
      <dl *ngIf="summary as counts" class="operations-metrics">
        <div>
          <dt>Pending</dt>
          <dd>{{ counts.pending }}</dd>
        </div>
        <div>
          <dt>Leased / in progress</dt>
          <dd>{{ counts.leased }}</dd>
        </div>
        <div>
          <dt>Failed / dead letter</dt>
          <dd>{{ counts.dead }}</dd>
        </div>
        <div>
          <dt>Completed</dt>
          <dd>{{ counts.completed }}</dd>
        </div>
        <div>
          <dt>Total attempts</dt>
          <dd>{{ counts.totalAttempts }}</dd>
        </div>
        <div>
          <dt>Without a consumer</dt>
          <dd>{{ counts.eventsWithoutConsumers }}</dd>
        </div>
      </dl>
      <p *ngIf="summary as counts" class="operations-muted">
        Retried deliveries: {{ counts.retriedDeliveries }} · oldest outstanding
        age:
        {{
          counts.oldestOutstandingAgeSeconds === null
            ? "none"
            : (counts.oldestOutstandingAgeSeconds | number: "1.0-0") +
              " seconds"
        }}
      </p>
      <section
        *ngIf="errors.summary as error"
        class="operations-error"
        role="alert"
      >
        <strong>{{ error.title }}</strong>
        <p *ngFor="let message of error.messages">{{ message }}</p>
        <button
          uiButton
          type="button"
          (click)="loadSummary()"
          [disabled]="busy"
        >
          Retry summary read
        </button>
      </section>
    </section>
    <section class="operations-worker" aria-labelledby="outbox-worker-title">
      <header class="operations-header">
        <h3 id="outbox-worker-title">Event outbox worker</h3>
        <button
          uiButton
          type="button"
          (click)="loadWorker()"
          [disabled]="busy || loading.worker"
        >
          Refresh worker state
        </button>
      </header>
      <p *ngIf="loading.worker" role="status">Loading worker control…</p>
      <section
        *ngIf="errors.worker as error"
        class="operations-error"
        role="alert"
      >
        <strong>{{ error.title }}</strong>
        <p *ngFor="let message of error.messages">{{ message }}</p>
      </section>
      <ng-container *ngIf="worker as state">
        <dl class="operations-facts">
          <div>
            <dt>Installed dispatch</dt>
            <dd>
              {{
                state.installationDispatchEnabled
                  ? "Enabled"
                  : "Disabled by installation configuration"
              }}
            </dd>
          </div>
          <div>
            <dt>Persisted worker gate</dt>
            <dd>{{ state.paused ? "Paused" : "Open" }}</dd>
          </div>
          <div>
            <dt>Effective dispatch</dt>
            <dd>
              {{
                state.effectiveDispatchEnabled
                  ? "May claim pending work"
                  : "Stopped · no new claims"
              }}
            </dd>
          </div>
          <div>
            <dt>Control revision</dt>
            <dd>{{ state.revision }}</dd>
          </div>
        </dl>
        <p class="operations-muted">
          Pause acknowledgement waits for the current atomic delivery, then
          stops new claims and execution of retained leases. Pending work and
          leases remain inspectable and recoverable. Resume clears the operator
          pause; installed dispatch configuration still governs execution.
        </p>
        <p class="operations-muted">
          This gate controls event outbox dispatch. Email, document generation
          and schedule projections keep their own controls.
        </p>
        <p *ngIf="!state.installationDispatchEnabled" class="operations-notice">
          Dispatch is disabled by installation configuration. This screen cannot
          enable it. Resume only clears the operator pause; installation
          configuration still prevents execution.
        </p>
        <p *ngIf="state.updatedUtc" class="operations-muted">
          Last control: {{ state.updatedUtc }} · actor
          {{ state.updatedByUserId }} · {{ state.lastReason }}
        </p>
        <form
          *ngIf="canAuthor()"
          class="operations-command"
          (ngSubmit)="changeWorker(!state.paused)"
        >
          <label
            >Worker-control reason
            <textarea
              uiControl
              name="workerReason"
              rows="2"
              maxlength="1000"
              [(ngModel)]="workerReason"
              [disabled]="busy"
              placeholder="Why pause or resume committed dispatch?"
            ></textarea></label
          ><button uiButton type="submit" [disabled]="!canChangeWorker">
            {{
              busy
                ? "Working…"
                : state.paused
                  ? "Resume worker"
                  : "Pause worker"
            }}
          </button>
        </form>
        <p *ngIf="staleWorker" class="operations-notice" role="status">
          The worker control changed. Refresh its exact revision before
          retrying; your reason is kept.
        </p>
      </ng-container>
      <details>
        <summary>Worker control history · latest 100</summary>
        <button
          uiButton
          type="button"
          (click)="loadWorkerHistory()"
          [disabled]="busy || loading.workerHistory"
        >
          {{
            loading.workerHistory
              ? "Loading…"
              : historyLoaded
                ? "Refresh control history"
                : "Load control history"
          }}
        </button>
        <p
          *ngIf="errors.workerHistory as error"
          class="operations-error"
          role="alert"
        >
          {{ error.messages.join(" ") }}
        </p>
        <p *ngIf="historyLoaded && !workerHistory.length">
          No worker pause or resume recorded.
        </p>
        <ol class="operations-recoveries">
          <li *ngFor="let control of workerHistory">
            <strong>{{ control.action | titlecase }}</strong> · revision
            {{ control.revision }} · {{ control.occurredUtc }} · actor
            {{ control.actorUserId }}
            <p>{{ control.reason }}</p>
          </li>
        </ol>
      </details>
    </section>
    <section
      class="operations-deliveries"
      aria-labelledby="outbox-deliveries-title"
    >
      <header class="operations-header">
        <h3 id="outbox-deliveries-title">Committed deliveries</h3>
        <label
          >Delivery state
          <select
            uiControl
            [(ngModel)]="status"
            (ngModelChange)="changeStatus()"
            [disabled]="busy || loading.page"
          >
            <option *ngFor="let choice of statuses" [value]="choice.code">
              {{ choice.label }}
            </option>
          </select></label
        >
      </header>
      <p *ngIf="loading.page" role="status">Loading delivery page…</p>
      <section
        *ngIf="errors.page as error"
        class="operations-error"
        role="alert"
      >
        <strong>{{ error.title }}</strong>
        <p *ngFor="let message of error.messages">{{ message }}</p>
        <button uiButton type="button" (click)="loadPage()" [disabled]="busy">
          Retry delivery read
        </button>
      </section>
      <p *ngIf="page && !rows.length && !loading.page">
        No committed deliveries match this state.
      </p>
      <div class="operations-grid" *ngIf="rows.length">
        <ui-grid
          [rowData]="rows"
          [columnDefs]="columns"
          [agGridOptions]="gridOptions"
          [showPanelHeader]="false"
          [showStandardFilterControls]="false"
          [showBlankRowsOnLastPage]="false"
          [autoHidePaginator]="true"
          containerClass="no-panel"
          width="fill"
        />
      </div>
      <div class="operations-actions" *ngIf="page">
        <button
          uiButton
          type="button"
          (click)="previousPage()"
          [disabled]="busy || loading.page || !canPreviousPage"
        >
          Previous page</button
        ><span>Showing {{ rows.length }} deliveries · up to 50 per page</span
        ><button
          uiButton
          type="button"
          (click)="nextPage()"
          [disabled]="busy || loading.page || !page.hasMore"
        >
          Next page
        </button>
      </div>
    </section>
    <section
      class="operations-detail"
      *ngIf="selectedIdentity"
      aria-labelledby="outbox-detail-title"
    >
      <header class="operations-header">
        <h3 id="outbox-detail-title">Selected committed invocation</h3>
        <button
          uiButton
          type="button"
          (click)="refreshDetail()"
          [disabled]="busy || loading.detail"
        >
          Refresh selected delivery
        </button>
      </header>
      <p *ngIf="loading.detail" role="status">
        Loading exact delivery identity and history…
      </p>
      <section
        *ngIf="errors.detail as error"
        class="operations-error"
        role="alert"
      >
        <strong>{{ error.title }}</strong>
        <p *ngFor="let message of error.messages">{{ message }}</p>
        <p>
          The previous selection is cleared. Refresh this delivery before taking
          an action.
        </p>
      </section>
      <ng-container *ngIf="detail as evidence">
        <dl class="operations-facts">
          <div>
            <dt>Operation / event</dt>
            <dd>
              {{ evidence.delivery.eventType }} ·
              {{
                evidence.delivery.operationKind === "module-invocation"
                  ? "Independent module invocation"
                  : evidence.delivery.operationKind
              }}
            </dd>
          </div>
          <div>
            <dt>Event identity</dt>
            <dd>{{ evidence.delivery.eventId }}</dd>
          </div>
          <div>
            <dt>Consumer invocation</dt>
            <dd>{{ evidence.delivery.consumerId }}</dd>
          </div>
          <div *ngIf="evidence.delivery.runId">
            <dt>Run identity</dt>
            <dd>{{ evidence.delivery.runId }}</dd>
          </div>
          <div *ngIf="evidence.delivery.invocationId">
            <dt>
              {{
                evidence.delivery.operationKind === "module-invocation"
                  ? "Module invocation identity"
                  : "Action invocation identity"
              }}
            </dt>
            <dd>{{ evidence.delivery.invocationId }}</dd>
          </div>
          <div *ngIf="evidence.delivery.supersedesInvocationId">
            <dt>New evaluation of</dt>
            <dd>{{ evidence.delivery.supersedesInvocationId }}</dd>
          </div>
          <div>
            <dt>Current progress</dt>
            <dd>
              {{ evidence.delivery.status }} ·
              {{ evidence.delivery.totalAttempts }} total attempts ·
              {{ evidence.delivery.attemptCount }} in current retry cycle
            </dd>
          </div>
          <div>
            <dt>Last failure issue</dt>
            <dd>{{ evidence.delivery.lastIssueCode || "None recorded" }}</dd>
          </div>
          <div>
            <dt>Committed at (UTC)</dt>
            <dd>{{ evidence.delivery.createdUtc }}</dd>
          </div>
          <div *ngIf="evidence.delivery.completedUtc">
            <dt>Completed at (UTC)</dt>
            <dd>{{ evidence.delivery.completedUtc }}</dd>
          </div>
          <div>
            <dt>Correlation</dt>
            <dd>{{ evidence.delivery.correlationId }}</dd>
          </div>
          <ng-container
            *ngIf="evidence.delivery.operationKind === 'module-invocation'"
          >
            <div>
              <dt>Registered trigger</dt>
              <dd>
                {{ evidence.delivery.triggerCode || "Not recorded" }} · version
                {{ evidence.delivery.triggerVersion ?? "not recorded" }}
              </dd>
            </div>
            <div>
              <dt>Source system</dt>
              <dd>{{ evidence.delivery.sourceSystem || "Not recorded" }}</dd>
            </div>
            <div>
              <dt>Source kind</dt>
              <dd>{{ evidence.delivery.sourceKind || "Not recorded" }}</dd>
            </div>
            <div>
              <dt>Source event identity</dt>
              <dd>{{ evidence.delivery.sourceEventId || "Not recorded" }}</dd>
            </div>
            <div>
              <dt>Module</dt>
              <dd>{{ evidence.delivery.moduleCode || "Not recorded" }}</dd>
            </div>
            <div>
              <dt>Command</dt>
              <dd>{{ evidence.delivery.commandCode || "Not recorded" }}</dd>
            </div>
          </ng-container>
        </dl>
        <p class="operations-muted">
          The original committed event and consumer identify this invocation.
          Attempts describe executions of that same work; retry does not repeat
          a completed internal effect.
        </p>
        <p
          *ngIf="evidence.delivery.metadataAvailable === false"
          class="operations-notice"
        >
          Some retained operation metadata is unavailable ({{
            evidence.delivery.metadataIssueCode
          }}). Delivery identity, state and recovery history remain inspectable;
          missing metadata is not inferred.
        </p>
        <p *ngIf="evidence.historyTruncated" class="operations-notice">
          Showing the latest 100 detailed attempts and latest 100 recovery
          records. Earlier history remains retained.
        </p>
        <section aria-labelledby="delivery-attempts-title">
          <h4 id="delivery-attempts-title">Execution attempts</h4>
          <p *ngIf="evidence.legacyAttemptCount" class="operations-muted">
            {{ evidence.legacyAttemptCount }} earlier attempts predate detailed
            attempt recording. Their aggregate count is retained.
          </p>
          <p *ngIf="!evidence.attempts.length">
            No detailed execution attempts recorded.
          </p>
          <div *ngIf="evidence.attempts.length" class="operations-history">
            <table>
              <thead>
                <tr>
                  <th scope="col">Attempt</th>
                  <th scope="col">Started (UTC)</th>
                  <th scope="col">State</th>
                  <th scope="col">Finished / issue</th>
                </tr>
              </thead>
              <tbody>
                <tr *ngFor="let attempt of evidence.attempts">
                  <td>
                    {{ attempt.attemptNumber
                    }}<small>{{ attempt.attemptId }}</small>
                  </td>
                  <td>{{ attempt.startedUtc }}</td>
                  <td>{{ attempt.status }}</td>
                  <td>
                    {{ attempt.finishedUtc || "No finish recorded"
                    }}<small>{{ attempt.issueCode }}</small>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>
        <section aria-labelledby="delivery-recovery-title">
          <h4 id="delivery-recovery-title">Audited recovery</h4>
          <p *ngIf="!evidence.recoveries.length">No operator retry recorded.</p>
          <ol class="operations-recoveries">
            <li *ngFor="let recovery of evidence.recoveries">
              <strong>Retry the same invocation</strong> ·
              {{ recovery.occurredUtc }} · actor {{ recovery.actorUserId }}
              <p>{{ recovery.reason }}</p>
              <small
                >{{ recovery.previousAttempts }} attempts before this retry ·
                {{ recovery.id }}</small
              >
            </li>
          </ol>
        </section>
        <form
          *ngIf="canAuthor() && evidence.delivery.status === 'dead'"
          class="operations-command"
          (ngSubmit)="retryInvocation()"
        >
          <label
            >Invocation retry reason
            <textarea
              uiControl
              name="retryReason"
              rows="2"
              maxlength="1000"
              [(ngModel)]="retryReason"
              [disabled]="busy"
              placeholder="What was repaired, and why is another attempt appropriate?"
            ></textarea></label
          ><button
            uiButton
            type="submit"
            severity="primary"
            [outlined]="false"
            [disabled]="!canRetry"
          >
            {{ busy ? "Working…" : "Retry this invocation" }}
          </button>
        </form>
        <p *ngIf="!canAuthor()" class="operations-muted">
          Platform administrator author permission is required for retry and
          worker controls.
        </p>
        <p *ngIf="staleDelivery" class="operations-notice" role="status">
          The delivery changed. Refresh the selected delivery before retrying;
          your reason is kept.
        </p>
        <p class="operations-muted">
          A new evaluation is a separate operation with a new invocation
          identity. Starting one is unavailable on this outbox surface ({{
            evidence.newEvaluation.reasonCode
          }}). Future activity, schedule and candidate providers can add their
          governed views here.
        </p>
      </ng-container>
    </section>
    <p *ngIf="validationError" class="operations-error" role="alert">
      {{ validationError }}
    </p>
    <section *ngIf="actionError as error" class="operations-error" role="alert">
      <strong>{{ error.title }}</strong>
      <p *ngFor="let message of error.messages">{{ message }}</p>
      <p>
        Your recovery reasons are kept. No automatic retry or worker enablement
        is performed.
      </p>
      <details
        *ngIf="
          error.problemCode || error.issueCodes?.length || error.correlationId
        "
      >
        <summary>Recovery issue details</summary>
        <p>{{ error.problemCode }} {{ error.issueCodes?.join(", ") }}</p>
        <p>{{ error.correlationId }}</p>
      </details>
    </section>
    <p
      *ngIf="actionNotice"
      class="operations-notice"
      role="status"
      aria-live="polite"
    >
      {{ actionNotice }}
    </p>
  </ng-container>
  <phms-recurring-schedules
    [interactionBlocked]="busy"
    (deliveryRequested)="openDelivery($event)"
  />
  <details
    *ngIf="canRead() && !accessDenied"
    (toggle)="openDeploymentConfiguration($event)"
  >
    <summary>Deployment configuration</summary>
    <p>
      Administrator settings for automatic hearing enrollment. Workflow
      eligibility tests inherit their published version and run independently of
      this configuration.
    </p>
    <phms-hearing-enrollment-setup
      *ngIf="deploymentConfigurationLoaded"
      [interactionBlocked]="busy"
    />
  </details>
</section>
